import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import 'swiper/css';

const CAROUSEL_SELECTOR = "[data-entry-carousel]";
const TREATMENTS_REVEAL_SELECTOR = ".entry-carousel--treatments";
const TREATMENTS_REVEAL_PENDING_CLASS = "entry-carousel--treatments-pending";
const TREATMENTS_REVEAL_VISIBLE_CLASS = "entry-carousel--treatments-visible";
const TEAM_REVEAL_SELECTOR = ".entry-carousel--team";
const TEAM_REVEAL_PENDING_CLASS = "entry-carousel--team-pending";
const TEAM_REVEAL_VISIBLE_CLASS = "entry-carousel--team-visible";

function visibleSlidesForViewport() {
	if (window.matchMedia("(min-width: 64rem)").matches) {
		return 3;
	}

	if (window.matchMedia("(min-width: 48rem)").matches) {
		return 2;
	}

	return 1;
}

function initTreatmentsCarouselReveals() {
	initCarouselReveal({
		pendingClass: TREATMENTS_REVEAL_PENDING_CLASS,
		selector: TREATMENTS_REVEAL_SELECTOR,
		visibleClass: TREATMENTS_REVEAL_VISIBLE_CLASS,
	});
}

function initTeamCarouselReveals() {
	initCarouselReveal({
		pendingClass: TEAM_REVEAL_PENDING_CLASS,
		selector: TEAM_REVEAL_SELECTOR,
		visibleClass: TEAM_REVEAL_VISIBLE_CLASS,
	});
}

function initCarouselReveal({ pendingClass, selector, visibleClass }) {
	const carousels = document.querySelectorAll(selector);

	if (!carousels.length) {
		return;
	}

	const revealCarousel = (carousel) => {
		carousel.classList.add(visibleClass);
		carousel.classList.remove(pendingClass);
	};

	if (!("IntersectionObserver" in window)) {
		for (const carousel of carousels) {
			revealCarousel(carousel);
		}

		return;
	}

	const observer = new IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				if (!entry.isIntersecting) {
					continue;
				}

				revealCarousel(entry.target);
				observer.unobserve(entry.target);
			}
		},
		{
			rootMargin: "0px 0px 20% 0px",
			threshold: 0.2,
		},
	);

	for (const carousel of carousels) {
		carousel.classList.add(pendingClass);
		observer.observe(carousel);

		window.requestAnimationFrame(() => {
			const bounds = carousel.getBoundingClientRect();
			const isNearViewport = bounds.top < window.innerHeight * 1.2 && bounds.bottom > 0;

			if (isNearViewport) {
				revealCarousel(carousel);
				observer.unobserve(carousel);
			}
		});

		window.setTimeout(() => {
			if (!carousel.classList.contains(visibleClass)) {
				revealCarousel(carousel);
				observer.unobserve(carousel);
			}
		}, 1200);
	}
}

export function initEntryCarousels() {
	initTreatmentsCarouselReveals();
	initTeamCarouselReveals();

	for (const carousel of document.querySelectorAll(CAROUSEL_SELECTOR)) {
		const swiperEl = carousel.querySelector("[data-entry-carousel-swiper]");
		const prevEl = carousel.querySelector("[data-entry-carousel-prev]");
		const nextEl = carousel.querySelector("[data-entry-carousel-next]");

		if (!swiperEl) {
			continue;
		}

		const slideCount = swiperEl.querySelectorAll(".swiper-slide").length;
		const shouldLoop = slideCount > visibleSlidesForViewport();
		const controls = carousel.querySelector(".entry-carousel__controls");
		let heightFrame = null;

		const syncSlideHeights = () => {
			if (heightFrame) {
				window.cancelAnimationFrame(heightFrame);
			}

			heightFrame = window.requestAnimationFrame(() => {
				heightFrame = null;
				const slides = [...swiperEl.querySelectorAll(".swiper-slide")];
				const items = slides.map((slide) => slide.firstElementChild).filter(Boolean);

				for (const item of items) {
					item.style.removeProperty("height");
				}

				const tallest = Math.max(0, ...items.map((item) => item.getBoundingClientRect().height));

				for (const item of items) {
					item.style.setProperty("height", `${tallest}px`);
				}
			});
		};

		const syncControls = (swiper) => {
			if (!controls) {
				return;
			}

			const slidesPerView = Number(swiper.params.slidesPerView) || 1;
			controls.hidden = slideCount < slidesPerView + 1;
		};

		const swiper = new Swiper(swiperEl, {
			modules: [Navigation],
			centerInsufficientSlides: true,
			loop: shouldLoop,
			rewind: false,
			grabCursor: true,
			speed: 450,
			watchOverflow: true,
			navigation: {
				prevEl,
				nextEl,
			},
			slidesPerView: 1,
			spaceBetween: 20,
			breakpoints: {
				768: {
					slidesPerView: 2,
					spaceBetween: 24,
				},
				1024: {
					slidesPerView: 3,
					spaceBetween: 32,
				},
			},
			on: {
				init(instance) {
					syncControls(instance);
					syncSlideHeights();
				},
				breakpoint(instance) {
					syncControls(instance);
					syncSlideHeights();
				},
				resize(instance) {
					syncControls(instance);
					syncSlideHeights();
				},
			},
		});

		syncControls(swiper);
		syncSlideHeights();

		for (const image of swiperEl.querySelectorAll("img")) {
			if (!image.complete) {
				image.addEventListener("load", syncSlideHeights, { once: true });
			}
		}
	}
}
